/*
 * Copyright (c) 2023-2024 Yesan SAS
 * All rights reserved
 *
 * The present software is the confidential and proprietary information of
 * Yesan SAS. You shall not disclose the present software and shall
 * use it only in accordance with the terms of the license agreement you
 * entered into with Yesan SAS. This software may be subject to
 * export or import laws in certain countries.
 */

import HUBUtils from 'HUB/satellites/utils/HUBUtils';


/**
 * HUB Internalization and locale management
 * @class HUBIntl
 * @author Vincent BONESTEVE
 */
export default class HUBIntl {
  /**
   * Fallback locale
   * @static
   */
  static _fallbackLocale = 'en';

  /**
   * Fallback short locale
   * @static
   */
  static _fallbackShortLocale = 'en';

  /**
   * Supported locales
   * @static
   */
  static _supportedLocales = ['en'];

  /**
   * NLS strings mapping
   * @static
   */
  static _strings = {};
  /**
   * NLS documents mapping
   * @static
   */
  static _documents = {};

  /**
   * User locale
   * @static
   */
  static _userLocale = HUBIntl.getDefaultLocale();
  /**
   * User short locale
   * @static
   */
  static _userShortLocale = HUBIntl.getDefaultShortLocale();

  /**
   * Get browser locales
   * @static
   * @returns default locales
   */
  static getBrowserLocales(){
    const browserLocales = (navigator.languages === undefined)?[navigator.language]:navigator.languages;
    if (!browserLocales){
      return null;
    }
    return browserLocales.map(locale => {return locale.trim();});
  }

  /**
   * Get default system locale or use fallback
   * @static
   * @returns default locale
   */
  static getDefaultLocale(){
    let locales = HUBIntl.getBrowserLocales();
    if (locales && HUBUtils.inArray(HUBIntl._supportedLocales,locales[0])){
      return locales[0];
    } else {
      return HUBIntl._fallbackLocale;
    }
  }

  /**
   * Get default system short locale or use fallback
   * @static
   * @returns default short locale
   */
  static getDefaultShortLocale(){
    let locales = HUBIntl.getBrowserLocales();
    if (locales){
      let locale = locales[0];
      if (locale.includes('-')){
        //Language only locale
        locale = locale.split('-')[0];
      }
      if (locale && HUBUtils.inArray(HUBIntl._supportedLocales,locale)){
        return locale;
      } else {
        return HUBIntl._fallbackShortLocale;
      }
    } else {
      return HUBIntl._fallbackShortLocale;
    }
  }

  /**
   * Get the translation of a string using the configured locale
   * @static
   * @param key string key
   * @param forcedLocale force locale for translation
   * @returns translated string value
   */
  static s(key,forcedLocale=null){
    if (!HUBUtils.isEmpty(key)){
      let locale = (forcedLocale)? forcedLocale:HUBIntl._userLocale;
      if (this._strings){
        if (this._strings[locale] && this._strings[locale][key]){
          //Country locale
          return this._strings[locale][key];
        } else if (locale.includes('-')){
          //Language only locale
          locale = locale.split('-')[0];
          if (this._strings[locale] && this._strings[locale][key]){
            return this._strings[locale][key];
          }
        }
      }
      //No matching locale fallback to "HUBIntl._fallbackLocale"
      if (forcedLocale != HUBIntl._fallbackLocale){
        let val = HUBIntl.s(key,HUBIntl._fallbackLocale);
        if (!HUBUtils.isEmpty(val)){
          return val;
        }
      }
    }
    return "";
  }

  /**
   * Get the translation of a document using the configured locale
   * @static
   * @param key string key
   * @param forcedLocale force locale for translation
   * @returns translated string value
   */
  static doc(key,forcedLocale=null){
    if (!HUBUtils.isEmpty(key)){
      let locale = (forcedLocale)? forcedLocale:HUBIntl._userLocale;
      if (this._documents){
        if (this._documents[locale] && this._documents[locale][key]){
          //Country locale
          return this._documents[locale][key];
        } else if (locale.includes('-')){
          //Language only locale
          locale = locale.split('-')[0];
          if (this._documents[locale] && this._documents[locale][key]){
            return this._documents[locale][key];
          }
        }
      }
      //No matching locale fallback to "HUBIntl._fallbackLocale"
      if (forcedLocale != HUBIntl._fallbackLocale){
        let val = HUBIntl.doc(key,HUBIntl._fallbackLocale);
        if (!HUBUtils.isEmpty(val)){
          return val;
        }
      }
    }
    return "";
  }

}
